import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { RescheduleDialog } from '@/components/reschedule-dialog';
import { useEntitlements } from '@/hooks/use-entitlements';
import { useRole } from '@/hooks/use-role';
import {
  updateJobVisit,
  type JobStatus,
  type JobVisitWithCrewDto,
  type PaymentMethod,
} from '@/lib/api/jobs';
import {
  createJobInvoice,
  jobInvoicesQueryKey,
  listJobInvoices,
} from '@/lib/api/invoices';
import { formatMoney } from '@/lib/utils';

import { AddInvoiceModal } from '../invoices/add-invoice-modal';
import { InvoiceDetailModal } from '../invoices/invoice-detail-modal';
import {
  invoicePhase,
  invoicePhaseColorClass,
  invoicePhaseLabel,
} from '../invoices/helpers';
import type { NewInvoiceSubmission } from '../invoices/types';
import {
  formatDate,
  paymentColorClass,
  statusColorClass,
  visitStatusLabel,
} from '../jobs/helpers';
import { RecordPaymentForm } from '../jobs/record-payment-form';
import { VisitCrewSection } from '../jobs/visit-crew-section';
import { isPartiallyPaid, nextStatus } from './helpers';

interface Props {
  visit: JobVisitWithCrewDto;
  businessId: string;
  jobId: string;
  jobTitle: string;
  jobDefaultPaymentMethod?: PaymentMethod | null;
  jobDefaultPaymentMethodId?: string | null;
}

export function VisitRow({
  visit,
  businessId,
  jobId,
  jobTitle,
  jobDefaultPaymentMethod,
  jobDefaultPaymentMethodId,
}: Props) {
  const { t } = useLanguage();
  const { isOwner, hasPermission } = useRole();
  const canManageInvoices = isOwner || hasPermission('create_invoices');
  const { has } = useEntitlements();
  const canManageCrew = has('employees');
  const [payOpen, setPayOpen] = useState(false);
  const [crewOpen, setCrewOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [sendInvoiceOpen, setSendInvoiceOpen] = useState(false);
  const [activeInvoiceId, setActiveInvoiceId] = useState<string | null>(null);
  const [skipArmed, setSkipArmed] = useState(false);
  const skipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (skipTimerRef.current) clearTimeout(skipTimerRef.current);
    },
    [],
  );

  const statusMutation = useMutation({
    mutationFn: (status: JobStatus) =>
      updateJobVisit(businessId, jobId, visit.id, { status }),
    meta: { successMessage: t.jobUpdated },
  });

  const rescheduleMutation = useMutation({
    mutationFn: (scheduledDate: string) =>
      updateJobVisit(businessId, jobId, visit.id, {
        scheduled_date: scheduledDate,
      }),
    meta: { successMessage: t.visitRescheduled },
  });

  const invoicesQuery = useQuery({
    queryKey: jobInvoicesQueryKey(businessId, jobId),
    queryFn: () => listJobInvoices(businessId, jobId),
    enabled: canManageInvoices && visit.status === 'completed',
  });
  const existingInvoice = invoicesQuery.data?.find(
    (inv) => inv.visit_id === visit.id,
  );

  const createInvoiceMutation = useMutation({
    mutationFn: (s: NewInvoiceSubmission) =>
      createJobInvoice(businessId, jobId, {
        total_amount: s.total_amount,
        payment_method_ids: s.payment_method_ids,
        visit_id: visit.id,
        subtotal: s.subtotal,
        tax: s.tax,
        discount: s.discount,
        due_date: s.due_date,
      }),
    meta: { successMessage: t.invoiceCreated },
    onSuccess: () => setSendInvoiceOpen(false),
  });

  const advance = nextStatus(visit.status);
  const terminal = visit.status === 'completed' || visit.status === 'cancelled';
  const partial = isPartiallyPaid(visit);

  function requestSkip() {
    if (skipTimerRef.current) clearTimeout(skipTimerRef.current);
    if (skipArmed) {
      setSkipArmed(false);
      statusMutation.mutate('cancelled');
      return;
    }
    setSkipArmed(true);
    skipTimerRef.current = setTimeout(() => setSkipArmed(false), 4000);
  }

  return (
    <li
      className={`rounded-lg border p-3 flex flex-col gap-2 ${
        visit.status === 'cancelled' ? 'opacity-60' : ''
      }`}
      data-testid={`occurrence-row-${visit.id}`}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => setCrewOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setCrewOpen((v) => !v);
        }}
        data-testid={`occurrence-rowbody-${visit.id}`}
        className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm cursor-pointer"
      >
        <span
          className={`font-medium w-28 ${
            visit.status === 'cancelled' ? 'line-through' : ''
          }`}
        >
          {formatDate(visit.scheduled_date)}
        </span>
        <span
          className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${statusColorClass(
            visit.status,
          )}`}
        >
          {visitStatusLabel(visit.status, t)}
        </span>
        <span
          className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${paymentColorClass(
            visit.payment_status,
            partial,
          )}`}
        >
          {partial
            ? t.paymentPartial
            : visit.payment_status === 'paid'
              ? t.paymentPaid
              : t.paymentUnpaid}
        </span>
        {existingInvoice &&
          invoicePhase(existingInvoice) === 'pending_confirmation' && (
            <span
              className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${invoicePhaseColorClass(
                'pending_confirmation',
              )}`}
            >
              {invoicePhaseLabel('pending_confirmation', t)}
            </span>
          )}
        <span className="text-xs text-muted-foreground flex-1 min-w-0 truncate">
          {visit.assigned_employees.length === 0
            ? '—'
            : visit.assigned_employees
                .map((c) =>
                  c.pay_amount != null
                    ? `${c.employee_full_name} (${formatMoney(c.pay_amount)})`
                    : c.employee_full_name,
                )
                .join(', ')}
        </span>
        <span className="text-xs w-20 text-right">
          {visit.total_amount != null ? formatMoney(visit.total_amount) : '—'}
        </span>
        <span className="text-xs w-20 text-right text-muted-foreground">
          {visit.amount_collected != null
            ? formatMoney(visit.amount_collected)
            : '—'}
        </span>
      </div>

      {!terminal && (
        <div className="flex flex-wrap gap-2">
          {advance && (
            <Button
              type="button"
              size="xs"
              variant="outline"
              disabled={statusMutation.isPending}
              onClick={() => statusMutation.mutate(advance)}
              data-testid={`occurrence-advance-${visit.id}`}
            >
              {advance === 'started'
                ? t.seriesAdvanceToStarted
                : t.seriesAdvanceToCompleted}
            </Button>
          )}
          {visit.payment_status === 'unpaid' && (
            <Button
              type="button"
              size="xs"
              variant="outline"
              onClick={() => setPayOpen((v) => !v)}
              data-testid={`occurrence-mark-paid-${visit.id}`}
            >
              {t.seriesMarkPaid}
            </Button>
          )}
          <Button
            type="button"
            size="xs"
            variant="outline"
            onClick={() => setRescheduleOpen(true)}
            data-testid={`occurrence-reschedule-${visit.id}`}
          >
            {t.reschedule}
          </Button>
          <Button
            type="button"
            size="xs"
            variant="ghost"
            className="text-destructive"
            disabled={statusMutation.isPending}
            onClick={requestSkip}
            data-testid={`occurrence-skip-${visit.id}`}
          >
            {skipArmed ? t.seriesSkipConfirm : t.seriesSkipVisit}
          </Button>
        </div>
      )}

      {visit.status === 'completed' && canManageInvoices && (
        <div className="flex flex-wrap gap-2">
          {existingInvoice ? (
            <Button
              type="button"
              size="xs"
              variant="outline"
              onClick={() => setActiveInvoiceId(existingInvoice.id)}
              data-testid={`occurrence-view-invoice-${visit.id}`}
            >
              {t.seriesViewInvoice}
            </Button>
          ) : (
            <Button
              type="button"
              size="xs"
              variant="outline"
              onClick={() => setSendInvoiceOpen(true)}
              data-testid={`occurrence-send-invoice-${visit.id}`}
            >
              {t.seriesSendInvoice}
            </Button>
          )}
        </div>
      )}

      {payOpen && visit.payment_status === 'unpaid' && (
        <div className="rounded-md border bg-muted/30 p-2">
          <RecordPaymentForm
            businessId={businessId}
            jobId={jobId}
            visitId={visit.id}
            totalAmount={visit.total_amount}
            paymentStatus={visit.payment_status}
            paymentMethod={visit.payment_method}
            paymentMethodId={visit.payment_method_id}
            onDone={() => setPayOpen(false)}
          />
        </div>
      )}

      {crewOpen && canManageCrew && (
        <div className="rounded-md border bg-muted/30 p-3">
          <VisitCrewSection
            businessId={businessId}
            jobId={jobId}
            visitId={visit.id}
          />
        </div>
      )}

      {rescheduleOpen && (
        <RescheduleDialog
          title={visitStatusLabel(visit.status, t)}
          currentDate={visit.scheduled_date}
          isPending={rescheduleMutation.isPending}
          onCancel={() => setRescheduleOpen(false)}
          onConfirm={(iso) =>
            rescheduleMutation.mutate(iso, {
              onSuccess: () => setRescheduleOpen(false),
            })
          }
        />
      )}

      {sendInvoiceOpen && (
        <AddInvoiceModal
          businessId={businessId}
          jobId={jobId}
          jobTitle={jobTitle}
          jobDefaultPaymentMethod={jobDefaultPaymentMethod}
          jobDefaultPaymentMethodId={jobDefaultPaymentMethodId}
          initialTotalAmount={visit.total_amount ?? undefined}
          onClose={() => setSendInvoiceOpen(false)}
          onSave={(s) => createInvoiceMutation.mutate(s)}
          isSaving={createInvoiceMutation.isPending}
        />
      )}

      {activeInvoiceId && (
        <InvoiceDetailModal
          businessId={businessId}
          jobId={jobId}
          invoiceId={activeInvoiceId}
          canManage={canManageInvoices}
          onClose={() => setActiveInvoiceId(null)}
        />
      )}
    </li>
  );
}
